// File Metadata:
// Filename: widgetSlice.js
// Version: 1.00
// Created: 2025-07-03
// Last Modified: 2025-07-03
// Authors: Dieuwer Descheemaecker, AI Assistant
// Description: Redux slice for managing widget state and user preferences

import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';

const initialState = {
  // User's enabled widgets
  enabledWidgets: [],
  
  // Widget configurations for each user
  widgetConfigurations: {},
  
  // Widget layout preferences
  layout: {
    type: 'mixed', // 'grid', 'mixed', 'tabbed'
    showNavigationButtons: true,
    compactMode: false
  },
  
  // Widget data cache
  widgetData: {},
  
  // Loading states
  loading: {
    preferences: false,
    data: {}
  },
  
  // Error states
  errors: {
    preferences: null,
    data: {}
  },
  
  // Last refresh timestamps
  lastRefresh: {}
};

const widgetSlice = createSlice({
  name: 'widgets',
  initialState,
  reducers: {
    // Enable/disable widgets
    toggleWidget: (state, action) => {
      const { widgetId, enabled } = action.payload;
      console.log('Redux toggleWidget:', { widgetId, enabled, currentEnabled: state.enabledWidgets });
      
      if (enabled) {
        if (!state.enabledWidgets.includes(widgetId)) {
          state.enabledWidgets.push(widgetId);
          console.log('Widget enabled:', widgetId);
        }
      } else {
        state.enabledWidgets = state.enabledWidgets.filter(id => id !== widgetId);
        console.log('Widget disabled:', widgetId);
      }
      
      console.log('New enabled widgets:', state.enabledWidgets);
    },

    // Set multiple enabled widgets
    setEnabledWidgets: (state, action) => {
      state.enabledWidgets = action.payload;
    },

    // Update widget configuration
    updateWidgetConfig: (state, action) => {
      const { widgetId, config } = action.payload;
      state.widgetConfigurations[widgetId] = {
        ...state.widgetConfigurations[widgetId],
        ...config
      };
    },

    // Set widget configuration
    setWidgetConfig: (state, action) => {
      const { widgetId, config } = action.payload;
      state.widgetConfigurations[widgetId] = config;
    },

    // Update layout preferences
    updateLayout: (state, action) => {
      state.layout = {
        ...state.layout,
        ...action.payload
      };
    },

    // Set widget data
    setWidgetData: (state, action) => {
      const { widgetId, data } = action.payload;
      state.widgetData[widgetId] = data;
      state.lastRefresh[widgetId] = Date.now();
      
      // Clear any previous errors for this widget
      if (state.errors.data[widgetId]) {
        delete state.errors.data[widgetId];
      }
    },

    // Set widget loading state
    setWidgetLoading: (state, action) => {
      const { widgetId, loading } = action.payload;
      state.loading.data[widgetId] = loading;
    },

    // Set widget error
    setWidgetError: (state, action) => {
      const { widgetId, error } = action.payload;
      state.errors.data[widgetId] = error;
      state.loading.data[widgetId] = false;
    },

    // Clear widget error
    clearWidgetError: (state, action) => {
      const { widgetId } = action.payload;
      if (state.errors.data[widgetId]) {
        delete state.errors.data[widgetId];
      }
    },

    // Set preferences loading state
    setPreferencesLoading: (state, action) => {
      state.loading.preferences = action.payload;
    },

    // Set preferences error
    setPreferencesError: (state, action) => {
      state.errors.preferences = action.payload;
      state.loading.preferences = false;
    },

    // Load user preferences
    loadUserPreferences: (state, action) => {
      console.log('=== REDUX LOAD USER PREFERENCES DEBUG ===');
      console.log('Raw action.payload:', action.payload);
      
      if (!action.payload) {
        console.error('No payload received in loadUserPreferences');
        return;
      }
      
      const { enabled, enabledWidgets, configurations, layout, compactMode, showNavigationButtons } = action.payload;
      
      console.log('Destructured values:', { enabled, enabledWidgets, configurations, layout, compactMode, showNavigationButtons });
      
      // Handle both backend format (enabled) and frontend format (enabledWidgets)
      // Ensure we have an array, not a boolean or other type
      let finalEnabledWidgets = [];
      if (Array.isArray(enabled)) {
        finalEnabledWidgets = enabled;
      } else if (Array.isArray(enabledWidgets)) {
        finalEnabledWidgets = enabledWidgets;
      } else {
        console.warn('Neither enabled nor enabledWidgets is an array:', { enabled, enabledWidgets });
        finalEnabledWidgets = [];
      }
      
      state.enabledWidgets = finalEnabledWidgets;
      state.widgetConfigurations = configurations || {};
      
      console.log('Final enabled widgets set:', finalEnabledWidgets);
      console.log('=== END REDUX DEBUG ===');
      
      // Handle layout - backend sends layout as string, frontend expects object
      if (typeof layout === 'string') {
        state.layout = { 
          ...state.layout, 
          type: layout,
          compactMode: compactMode || false,
          showNavigationButtons: showNavigationButtons !== false // default to true
        };
      } else {
        state.layout = { ...state.layout, ...layout };
      }
      
      state.loading.preferences = false;
      state.errors.preferences = null;
    },

    // Reset widget state
    resetWidgets: (state) => {
      return initialState;
    },

    // Reorder widgets
    reorderWidgets: (state, action) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const [removed] = state.enabledWidgets.splice(sourceIndex, 1);
      state.enabledWidgets.splice(destinationIndex, 0, removed);
    },

    // Update refresh timestamp
    updateRefreshTimestamp: (state, action) => {
      const { widgetId } = action.payload;
      state.lastRefresh[widgetId] = Date.now();
    }
  }
});

// Action creators
export const {
  toggleWidget,
  setEnabledWidgets,
  updateWidgetConfig,
  setWidgetConfig,
  updateLayout,
  setWidgetData,
  setWidgetLoading,
  setWidgetError,
  clearWidgetError,
  setPreferencesLoading,
  setPreferencesError,
  loadUserPreferences,
  resetWidgets,
  reorderWidgets,
  updateRefreshTimestamp
} = widgetSlice.actions;

// Alias for compatibility
export const setUserPreferences = loadUserPreferences;

// Selectors
export const selectEnabledWidgets = (state) => state.widgets.enabledWidgets;
export const selectWidgetConfigurations = (state) => state.widgets.widgetConfigurations;
export const selectLayout = (state) => state.widgets.layout;
export const selectWidgetData = (state) => state.widgets.widgetData;
export const selectWidgetLoading = (state) => state.widgets.loading;
export const selectWidgetErrors = (state) => state.widgets.errors;
export const selectLastRefresh = (state) => state.widgets.lastRefresh;

// Empty config constant to prevent new object creation
const EMPTY_CONFIG = {};

// Specific widget selectors with memoization
export const selectWidgetConfig = createSelector(
  [
    (state) => state.widgets.widgetConfigurations,
    (state, widgetId) => widgetId
  ],
  (configurations, widgetId) => configurations[widgetId] || EMPTY_CONFIG
);

export const selectWidgetDataById = (widgetId) => (state) => 
  state.widgets.widgetData[widgetId];

export const selectWidgetLoadingById = (widgetId) => (state) => 
  state.widgets.loading.data[widgetId] || false;

export const selectWidgetErrorById = (widgetId) => (state) => 
  state.widgets.errors.data[widgetId];

export const selectIsWidgetEnabled = (widgetId) => (state) => 
  state.widgets.enabledWidgets.includes(widgetId);

// Complex selectors
export const selectWidgetNeedsRefresh = (widgetId, refreshInterval) => (state) => {
  const lastRefresh = state.widgets.lastRefresh[widgetId];
  if (!lastRefresh) return true;
  
  return Date.now() - lastRefresh > refreshInterval;
};

export default widgetSlice.reducer;
